"use client"

import * as React from "react"
import { useEffect, useRef } from "react"
import { Link, useLocation } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { BrokerAccountSwitcher } from "@/components/broker-account-switcher"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { NavNotificationsCentreCategoryItem } from "@/components/nav-notifications-centre-category-item"
import { NavSidebarBackItem } from "@/components/nav-sidebar-back-item"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  useSidebar,
} from "@/components/ui/sidebar"
import { NavUser } from "@/components/nav-user"
import {
  NOTIFICATIONS_CENTRE_SIDEBAR_CATEGORY_LINKS,
  NOTIFICATIONS_CENTRE_INBOX_HEADER_CLASS,
  NOTIFICATIONS_CENTRE_INBOX_HEADER_INNER_CLASS,
  NOTIFICATIONS_CENTRE_INBOX_LINK,
  NOTIFICATIONS_CENTRE_INBOX_LIST_CLASS,
  NOTIFICATIONS_CENTRE_RETURN_PATH_KEY,
  NOTIFICATIONS_CENTRE_SIDEBAR_WIDTH,
  notificationsCentreCategoryNotificationPath,
  notificationsCentreListLabel,
  notificationsCentreNotificationPath,
  resolveNotificationsCentreCategory,
  resolveNotificationsCentreNotificationId,
} from "@/constants/notificationsCentre"
import { useNotificationsInbox } from "@/contexts/NotificationsInboxContext"
import {
  notificationsCentreInboxItemClass,
  sidebarGray500Class,
  sidebarTropicalSurfClass,
} from "@/constants/sidebarChrome"
import { cn } from "@/lib/utils"
import { applyDataTableRowCheckboxSelection } from "@/components/ui/data-table-row-selection"
import {
  getPerformanceSummaryPreviewText,
  isPerformanceSummaryEventKey,
} from "@/utils/notificationDisplay"
import { notificationStyleSeedCount } from "@/utils/notificationStyleSeed"
import { toast } from "sonner"

function resolveNotificationsCentreReturnPath(locationState: unknown): string {
  const statePath = (locationState as { fromSidebarPath?: string } | null)
    ?.fromSidebarPath
  const storedPath = window.sessionStorage.getItem(
    NOTIFICATIONS_CENTRE_RETURN_PATH_KEY
  )
  return (
    [statePath, storedPath].find(
      (path): path is string => !!path && !path.startsWith("/notifications")
    ) ?? "/dashboard"
  )
}

function getNotificationPreview(notification: {
  eventKey?: string | null
  message?: string
  details?: import("@/types/notifications").NotificationDataPayload | null
}): string | null {
  if (isPerformanceSummaryEventKey(notification.eventKey)) {
    return getPerformanceSummaryPreviewText(notification.details) ?? null
  }
  if (!notification.message) return null
  return notification.message.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
}

/**
 * sidebar-09 nested layout: icon rail (folders) + scrollable inbox list panel.
 */
export function NavNotificationsCentre({
  ...props
}: React.ComponentProps<typeof Sidebar>): React.ReactElement {
  const { pathname, state: locationState } = useLocation()
  const { isMobile, setOpen, setOpenMobile } = useSidebar()
  const {
    notifications,
    unreadCount,
    categoryFilter,
    fetching,
    loadError,
    hasMore,
    isLoadingMore,
    reload,
    loadMore,
    markAllRead,
    seedStyleNotifications,
    removeMany,
    removeMatching,
    selectNotification,
    handleListScroll,
    selectedNotification,
  } = useNotificationsInbox()

  const listRef = useRef<HTMLDivElement>(null)
  const [unreadsOnly, setUnreadsOnly] = React.useState(false)
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set())
  /** True when selection covers every matching notification (including not-yet-loaded pages). */
  const [selectAllMatching, setSelectAllMatching] = React.useState(false)
  const [isBulkDeleting, setIsBulkDeleting] = React.useState(false)
  /** Anchor for Shift+click multi-select in the inbox list. */
  const lastSelectedIdRef = useRef<string | null>(null)
  const shiftSelectHandledRef = useRef(false)
  const styleSeedAppliedRef = useRef(false)
  const showStyleSeedControl =
    process.env.NODE_ENV === "development" ||
    new URLSearchParams(window.location.search).get("seedStyleNotifications") === "1"

  useEffect(() => {
    if (!isMobile) {
      setOpen(true)
    }
  }, [isMobile, setOpen])

  useEffect(() => {
    if (styleSeedAppliedRef.current) return
    const params = new URLSearchParams(window.location.search)
    if (params.get("seedStyleNotifications") !== "1") return
    styleSeedAppliedRef.current = true
    const count = seedStyleNotifications()
    toast.success(`Loaded ${count} style sample notifications`)
  }, [seedStyleNotifications])

  const handleLoadStyleSamples = React.useCallback(() => {
    const count = seedStyleNotifications()
    toast.success(`Loaded ${count} style sample notifications`)
  }, [seedStyleNotifications])

  const closeMobileSidebar = React.useCallback(() => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }, [isMobile, setOpenMobile])

  useEffect(() => {
    const el = listRef.current
    if (!el || fetching || isLoadingMore || !hasMore) return
    if (el.scrollHeight <= el.clientHeight + 4) {
      void loadMore()
    }
  }, [notifications.length, fetching, hasMore, isLoadingMore, loadMore])

  // Drop selection when switching inbox/category or unreads filter.
  useEffect(() => {
    setSelectedIds(new Set())
    setSelectAllMatching(false)
    lastSelectedIdRef.current = null
  }, [categoryFilter, unreadsOnly])

  // Drop ids that are no longer in the loaded list (deleted elsewhere, etc.).
  useEffect(() => {
    if (selectAllMatching) return
    const present = new Set(notifications.map((n) => n.id))
    setSelectedIds((prev) => {
      let changed = false
      const next = new Set<string>()
      prev.forEach((id) => {
        if (present.has(id)) next.add(id)
        else changed = true
      })
      return changed ? next : prev
    })
  }, [notifications, selectAllMatching])

  useEffect(() => {
    if (selectedIds.size === 0) {
      lastSelectedIdRef.current = null
    }
  }, [selectedIds])

  const returnPath = React.useMemo(
    () => resolveNotificationsCentreReturnPath(locationState),
    [locationState]
  )

  const handleBackNavigate = React.useCallback(() => {
    window.sessionStorage.removeItem(NOTIFICATIONS_CENTRE_RETURN_PATH_KEY)
    closeMobileSidebar()
  }, [closeMobileSidebar])

  const activeNotificationId =
    selectedNotification?.id ?? resolveNotificationsCentreNotificationId(pathname)

  const activeCategory =
    categoryFilter ?? resolveNotificationsCentreCategory(pathname)

  const listTitle = notificationsCentreListLabel(activeCategory)

  const visibleNotifications = unreadsOnly
    ? notifications.filter((notification) => !notification.isRead)
    : notifications

  const visibleIds = React.useMemo(
    () => visibleNotifications.map((n) => n.id),
    [visibleNotifications]
  )

  const selectedVisibleCount = React.useMemo(() => {
    if (selectAllMatching) return visibleIds.length
    let count = 0
    for (const id of visibleIds) {
      if (selectedIds.has(id)) count += 1
    }
    return count
  }, [selectAllMatching, selectedIds, visibleIds])

  const allVisibleSelected =
    visibleIds.length > 0 &&
    (selectAllMatching || selectedVisibleCount === visibleIds.length)
  const someVisibleSelected =
    !selectAllMatching && selectedVisibleCount > 0 && !allVisibleSelected
  const selectAllChecked: boolean | "indeterminate" =
    selectAllMatching || allVisibleSelected
      ? true
      : someVisibleSelected
        ? "indeterminate"
        : false

  const canSelectAllMatching =
    allVisibleSelected && hasMore && !selectAllMatching && visibleIds.length > 0

  const toggleSelectAllVisible = React.useCallback(
    (checked: boolean | "indeterminate") => {
      const shouldSelect = checked === true
      if (!shouldSelect) {
        setSelectAllMatching(false)
        lastSelectedIdRef.current = null
      } else if (visibleIds.length > 0) {
        lastSelectedIdRef.current = visibleIds[visibleIds.length - 1] ?? null
      }
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (shouldSelect) {
          visibleIds.forEach((id) => next.add(id))
        } else {
          visibleIds.forEach((id) => next.delete(id))
        }
        return next
      })
    },
    [visibleIds]
  )

  const toggleSelectOne = React.useCallback(
    (id: string, checked: boolean | "indeterminate", shiftKey = false) => {
      const selected = checked === true

      if (selectAllMatching && !selected && !shiftKey) {
        // Leaving "all matching" mode: keep other loaded rows selected.
        setSelectAllMatching(false)
        setSelectedIds(new Set(visibleIds.filter((visibleId) => visibleId !== id)))
        lastSelectedIdRef.current = id
        return
      }

      if (shiftKey && selectAllMatching) {
        setSelectAllMatching(false)
      }

      setSelectedIds((prev) => {
        const previous: Record<string, boolean> = {}
        prev.forEach((selectedId) => {
          previous[selectedId] = true
        })
        // When in select-all-matching, treat all loaded rows as selected for the range base.
        const base =
          selectAllMatching
            ? (() => {
                const full: Record<string, boolean> = { ...previous }
                visibleIds.forEach((visibleId) => {
                  full[visibleId] = true
                })
                return full
              })()
            : previous

        const { next, nextAnchorRowId } = applyDataTableRowCheckboxSelection({
          previous: base,
          visibleRowIds: visibleIds,
          anchorRowId: lastSelectedIdRef.current,
          targetRowId: id,
          selected: shiftKey ? true : selected,
          shiftKey,
        })
        lastSelectedIdRef.current = nextAnchorRowId
        const out = new Set<string>()
        Object.keys(next).forEach((key) => {
          if (next[key]) out.add(key)
        })
        return out
      })

      if (shiftKey) shiftSelectHandledRef.current = true
    },
    [selectAllMatching, visibleIds]
  )

  const handleSelectAllMatching = React.useCallback(() => {
    setSelectAllMatching(true)
    setSelectedIds(new Set(visibleIds))
    lastSelectedIdRef.current =
      visibleIds.length > 0 ? visibleIds[visibleIds.length - 1] : null
  }, [visibleIds])

  const handleBulkDelete = React.useCallback(async () => {
    if (isBulkDeleting) return
    const useMatchingDelete =
      selectAllMatching || (allVisibleSelected && !hasMore && selectedVisibleCount > 0)
    if (!useMatchingDelete && selectedIds.size === 0) return

    setIsBulkDeleting(true)
    try {
      if (useMatchingDelete) {
        await removeMatching({ unreadOnly: unreadsOnly })
        toast.success(
          unreadsOnly
            ? `Deleted unread ${listTitle.toLowerCase()} notifications`
            : `Deleted ${listTitle.toLowerCase()} notifications`
        )
      } else {
        const ids = Array.from(selectedIds)
        await removeMany(ids)
        toast.success(
          ids.length === 1
            ? "Notification deleted"
            : `${ids.length} notifications deleted`
        )
      }
      setSelectedIds(new Set())
      setSelectAllMatching(false)
    } catch (error) {
      console.error("Bulk delete failed:", error)
      toast.error("Couldn't delete notifications. Try again.")
    } finally {
      setIsBulkDeleting(false)
    }
  }, [
    allVisibleSelected,
    hasMore,
    isBulkDeleting,
    listTitle,
    removeMany,
    removeMatching,
    selectAllMatching,
    selectedIds,
    selectedVisibleCount,
    unreadsOnly,
  ])

  const categoryNavLinks = (
    <>
      <NavNotificationsCentreCategoryItem
        link={NOTIFICATIONS_CENTRE_INBOX_LINK}
        active={!activeCategory}
        iconRailOnly={!isMobile}
        onNavigate={closeMobileSidebar}
      />
      {NOTIFICATIONS_CENTRE_SIDEBAR_CATEGORY_LINKS.map((link) => (
        <NavNotificationsCentreCategoryItem
          key={link.id}
          link={link}
          active={activeCategory === link.id}
          iconRailOnly={!isMobile}
          onNavigate={closeMobileSidebar}
        />
      ))}
    </>
  )

  const handleUnreadsToggle = (checked: boolean) => {
    setUnreadsOnly(checked)
  }

  const brokerSwitcherHeader = (
    <div
      className={cn(
        "flex w-full min-h-0 items-center",
        "group-data-[state=expanded]:h-12",
        "group-data-[state=collapsed]:h-8"
      )}
    >
      <BrokerAccountSwitcher />
    </div>
  )

  const inboxHeaderRow = (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <div
          className={cn(
            "truncate font-medium leading-snug",
            sidebarTropicalSurfClass,
            "text-sm md:text-[0.9375rem]"
          )}
        >
          {listTitle}
        </div>
        {unreadCount > 0 ? (
          <Badge
            data-notification-unread-badge=""
            variant="pine-compose-active"
            className="h-auto shrink-0 rounded-[6px] px-2 py-0.5 font-[family-name:'IBM_Plex_Sans',sans-serif] text-[10px] font-medium uppercase tracking-normal"
          >
            {unreadCount} New
          </Badge>
        ) : null}
      </div>
      <div
        data-show-stats-toggle=""
        className="flex shrink-0 items-center gap-2"
      >
        <Label
          htmlFor="notifications-centre-unreads-only"
          className={cn(
            "mb-0 text-sm font-normal leading-none",
            sidebarTropicalSurfClass
          )}
        >
          Unreads
        </Label>
        <Switch
          id="notifications-centre-unreads-only"
          checked={unreadsOnly}
          onCheckedChange={handleUnreadsToggle}
        />
      </div>
    </div>
  )

  const renderListToolbar = (itemPaddingClass: string) => {
    const showSelectControls = visibleNotifications.length > 0
    if (!showSelectControls && unreadCount <= 0 && !showStyleSeedControl) {
      return null
    }

    const selectionLabel = selectAllMatching
      ? unreadsOnly
        ? `All unread in ${listTitle}`
        : `All in ${listTitle}`
      : selectedVisibleCount > 0
        ? hasMore && allVisibleSelected
          ? `${selectedVisibleCount} loaded selected`
          : `${selectedVisibleCount} selected`
        : "Select all"

    const showDelete =
      selectAllMatching || selectedVisibleCount > 0 || selectedIds.size > 0

    return (
      <div
        className={cn(
          "flex flex-col gap-1 pb-1 pt-0",
          itemPaddingClass.includes("p-3") ? "px-3 pt-1" : "px-2 pt-1"
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          {showSelectControls ? (
            <div className="flex min-w-0 items-center gap-2">
              <Checkbox
                id="notifications-centre-select-all"
                data-notifications-inbox-checkbox=""
                checked={selectAllChecked}
                onCheckedChange={toggleSelectAllVisible}
                aria-label="Select all loaded notifications"
              />
              <Label
                htmlFor="notifications-centre-select-all"
                className={cn(
                  "mb-0 cursor-pointer text-[11px] font-normal leading-none",
                  sidebarGray500Class
                )}
              >
                {selectionLabel}
              </Label>
            </div>
          ) : (
            <span />
          )}
          <div className="flex shrink-0 items-center gap-3">
            {showDelete ? (
              <button
                type="button"
                data-notifications-bulk-delete=""
                disabled={isBulkDeleting}
                className="p-0 text-[11px] font-normal leading-none text-[color:var(--brand-glacial-red)] underline underline-offset-2 font-[family-name:var(--font-primary)] hover:opacity-80 disabled:opacity-50"
                onClick={() => {
                  void handleBulkDelete()
                }}
              >
                Delete
              </button>
            ) : null}
            {unreadCount > 0 && visibleNotifications.length > 0 ? (
              <button
                type="button"
                data-notifications-mark-all-read=""
                className="p-0 text-[11px] font-normal leading-none text-[#6B7280] underline underline-offset-2 font-[family-name:var(--font-primary)] hover:opacity-80 dark:text-white"
                onClick={markAllRead}
              >
                Mark all as read
              </button>
            ) : null}
            {showStyleSeedControl ? (
              <button
                type="button"
                data-notifications-load-style-samples=""
                className="p-0 text-[11px] font-normal leading-none text-[#6B7280] underline underline-offset-2 font-[family-name:var(--font-primary)] hover:opacity-80 dark:text-white"
                onClick={handleLoadStyleSamples}
              >
                Load style samples ({notificationStyleSeedCount()})
              </button>
            ) : null}
          </div>
        </div>
        {canSelectAllMatching ? (
          <button
            type="button"
            data-notifications-select-all-matching=""
            className="self-start p-0 text-[11px] font-normal leading-none text-[color:var(--brand-tropical-surf)] underline underline-offset-2 font-[family-name:var(--font-primary)] hover:opacity-80"
            onClick={handleSelectAllMatching}
          >
            {unreadsOnly
              ? `Select all unread in ${listTitle}`
              : `Select all in ${listTitle}`}
          </button>
        ) : null}
      </div>
    )
  }

  const renderNotificationList = (itemPaddingClass: string) => (
    <>
      {renderListToolbar(itemPaddingClass)}
      {loadError && !fetching && notifications.length === 0 ? (
        <div className={cn("flex flex-col gap-2", itemPaddingClass)}>
          <p className="text-sm text-muted-foreground">
            Couldn&apos;t load notifications.
          </p>
          <button
            type="button"
            className="text-left text-sm text-[color:var(--brand-tropical-surf)]"
            onClick={reload}
          >
            Try again
          </button>
        </div>
      ) : !fetching && visibleNotifications.length === 0 ? (
        <p className={cn(itemPaddingClass, "text-sm text-muted-foreground")}>
          {unreadsOnly
            ? `No unread ${listTitle.toLowerCase()} notifications`
            : `No ${listTitle.toLowerCase()} notifications`}
        </p>
      ) : (
        visibleNotifications.map((notification) => {
          const isActive = activeNotificationId === notification.id
          const previewText = getNotificationPreview(notification)
          const notificationPath = activeCategory
            ? notificationsCentreCategoryNotificationPath(activeCategory, notification.id)
            : notificationsCentreNotificationPath(notification.id)

          const isUnread = !notification.isRead
          const isChecked = selectAllMatching || selectedIds.has(notification.id)

          return (
            <div
              key={notification.id}
              data-notification-inbox-item=""
              data-active={isActive || undefined}
              data-unread={isUnread || undefined}
              data-selected={isChecked || undefined}
              className={cn(
                notificationsCentreInboxItemClass,
                "flex items-start gap-2 text-sm leading-tight",
                itemPaddingClass,
                isActive && "bg-sidebar-accent"
              )}
            >
              <Checkbox
                data-notifications-inbox-checkbox=""
                checked={isChecked}
                onPointerDown={(event) => {
                  if (event.button !== 0) return
                  if (!event.shiftKey) {
                    shiftSelectHandledRef.current = false
                    return
                  }
                  // Own Shift+range so focus ring does not replace multi-select.
                  event.preventDefault()
                  event.stopPropagation()
                  toggleSelectOne(notification.id, true, true)
                }}
                onCheckedChange={(checked) => {
                  if (shiftSelectHandledRef.current) {
                    shiftSelectHandledRef.current = false
                    return
                  }
                  toggleSelectOne(notification.id, checked)
                }}
                onClick={(event) => event.stopPropagation()}
                aria-label={`Select ${notification.title}`}
                className="mt-0.5"
              />
              <Link
                to={notificationPath}
                aria-current={isActive ? "page" : undefined}
                onClick={(e) => {
                  e.preventDefault()
                  selectNotification(notification)
                  closeMobileSidebar()
                }}
                className="flex min-w-0 flex-1 flex-col items-start gap-2"
              >
                <div className="flex w-full items-center gap-2">
                  <div
                    className={cn(
                      "min-w-0 flex-1 truncate",
                      isUnread ? "font-semibold" : "font-medium",
                      sidebarTropicalSurfClass
                    )}
                  >
                    {notification.title}
                  </div>
                  <div
                    className={cn(
                      "ml-auto shrink-0 text-xs",
                      sidebarGray500Class
                    )}
                  >
                    {notification.time}
                  </div>
                </div>
                {previewText ? (
                  <div
                    className={cn(
                      "line-clamp-2 w-full text-xs whitespace-break-spaces",
                      sidebarGray500Class
                    )}
                  >
                    {previewText}
                  </div>
                ) : null}
              </Link>
            </div>
          )
        })
      )}

      {isLoadingMore ? (
        <p className={cn(itemPaddingClass, "text-xs text-muted-foreground")}>
          Loading more…
        </p>
      ) : null}
      {!fetching && visibleNotifications.length > 0 && hasMore ? (
        <p
          className={cn(
            itemPaddingClass,
            "text-center text-[11px] text-muted-foreground"
          )}
        >
          Scroll for older notifications
        </p>
      ) : null}
      {!fetching && !isLoadingMore && visibleNotifications.length > 0 && !hasMore ? (
        <p
          className={cn(
            itemPaddingClass,
            "text-center text-[11px] text-muted-foreground"
          )}
        >
          Showing {visibleNotifications.length} notification
          {visibleNotifications.length === 1 ? "" : "s"}
          {unreadsOnly ? " (unreads)" : ""}
        </p>
      ) : null}
    </>
  )

  if (isMobile) {
    return (
      <Sidebar
        collapsible="icon"
        className="w-full max-w-[min(100vw,var(--sidebar-width))]"
        style={
          {
            "--sidebar-width": NOTIFICATIONS_CENTRE_SIDEBAR_WIDTH,
          } as React.CSSProperties
        }
        data-testid="shadcn-sidebar-notifications-centre"
        {...props}
      >
        <SidebarHeader>{brokerSwitcherHeader}</SidebarHeader>
        <SidebarContent>
          <SidebarGroup className="animate-in fade-in-0 slide-in-from-left-1 duration-200">
            <SidebarMenu>
              <NavSidebarBackItem
                returnPath={returnPath}
                onNavigate={handleBackNavigate}
              />
            </SidebarMenu>
          </SidebarGroup>
          <SidebarGroup className="border-b">
            <SidebarMenu>{categoryNavLinks}</SidebarMenu>
          </SidebarGroup>
          <SidebarGroup className="border-b">
            <div className="px-2">{inboxHeaderRow}</div>
          </SidebarGroup>
          <SidebarGroup className="min-h-0 flex-1">
            <SidebarGroupContent
              ref={listRef}
              className="flex max-h-[calc(100svh-14rem)] min-h-0 flex-1 flex-col gap-0 overflow-y-auto"
              onScroll={handleListScroll}
            >
              {renderNotificationList("px-2 py-3")}
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <NavUser />
        </SidebarFooter>
      </Sidebar>
    )
  }

  return (
    <Sidebar
      collapsible="icon"
      className="overflow-hidden"
      data-testid="shadcn-sidebar-notifications-centre"
      {...props}
    >
      <div className="flex h-full min-h-0 w-full flex-row items-stretch">
      {/* Icon rail — same width as collapsed dashboard sidebar (3rem) */}
      <Sidebar
        collapsible="none"
        data-notifications-category-rail=""
        className="!w-(--sidebar-width-icon) shrink-0"
      >
        <SidebarHeader>
          <div className="flex h-12 w-full min-h-0 items-center">
            <BrokerAccountSwitcher forceIconRail />
          </div>
        </SidebarHeader>
        <SidebarContent className="min-h-0 overflow-y-auto">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <NavSidebarBackItem
                  returnPath={returnPath}
                  onNavigate={handleBackNavigate}
                  iconRailOnly
                />
                {categoryNavLinks}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <NavUser iconRailOnly />
        </SidebarFooter>
      </Sidebar>

      {/* Inbox list panel — sidebar-09 second column */}
      <Sidebar
        collapsible="none"
        className="flex h-full min-h-0 min-w-0 flex-1 flex-col pb-2"
      >
        <div
          data-notifications-inbox-header=""
          className={NOTIFICATIONS_CENTRE_INBOX_HEADER_CLASS}
        >
          <div className={NOTIFICATIONS_CENTRE_INBOX_HEADER_INNER_CLASS}>
            {inboxHeaderRow}
          </div>
        </div>
        <SidebarContent className="min-h-0 flex-1 overflow-hidden">
          <SidebarGroup className="flex min-h-0 flex-1 flex-col px-0 py-0">
            <SidebarGroupContent
              ref={listRef}
              className={NOTIFICATIONS_CENTRE_INBOX_LIST_CLASS}
              onScroll={handleListScroll}
            >
              {renderNotificationList("p-3")}
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
      </div>
    </Sidebar>
  )
}
